// Defaults shared by the shipped themes, plus the geometry no theme may
// change. A theme spreads the defaults and overrides a slice — e.g. a
// different font or a rounder radius scale just overrides
// `typography.fonts` or `radius`. Kept here so glass.ts / earth.ts /
// midnight.ts read as "what's different about this theme".

import type {
  RampKey,
  RampStep,
  ThemeRadius,
  ThemeTypography,
} from "./types";

const PLEX_SANS =
  '"IBM Plex Sans", system-ui, -apple-system, "Segoe UI", Helvetica, Arial, sans-serif';
const PLEX_MONO =
  '"IBM Plex Mono", ui-monospace, SFMono-Regular, Menlo, Consolas, monospace';
const PLEX_DISPLAY = '"IBM Plex Sans", system-ui, -apple-system, sans-serif';

// Per-theme typeface stacks (loaded in index.html). One font = one
// design language.
export const FONTS = {
  plexSans: PLEX_SANS,
  plexMono: PLEX_MONO,
  hanken: '"Hanken Grotesk", system-ui, -apple-system, sans-serif',
  // Soft, low-contrast old-style serif. Chosen over a high-contrast display
  // face (Instrument Serif et al) on two counts: those are drawn narrow, so
  // headings read compressed, and their hairline strokes go fragile on dark
  // surfaces. Fraunces is wide, warm, and holds weight on espresso.
  fraunces: '"Fraunces", Georgia, "Times New Roman", serif',
  inter:
    '"Inter", system-ui, -apple-system, "Segoe UI", Helvetica, Arial, sans-serif',
};

// Spread baseTypography and override slices (deep-merges fonts / label /
// display) so a theme states only what's different about its type.
export function makeTypography(o: {
  fonts?: Partial<ThemeTypography["fonts"]>;
  bodyWeight?: number;
  label?: Partial<ThemeTypography["label"]>;
  display?: Partial<ThemeTypography["display"]>;
  linkUnderline?: boolean;
}): ThemeTypography {
  return {
    ...baseTypography,
    bodyWeight: o.bodyWeight ?? baseTypography.bodyWeight,
    linkUnderline: o.linkUnderline ?? baseTypography.linkUnderline,
    fonts: { ...baseTypography.fonts, ...o.fonts },
    label: { ...baseTypography.label, ...o.label },
    display: { ...baseTypography.display, ...o.display },
  };
}

export const baseTypography: ThemeTypography = {
  fonts: { sans: PLEX_SANS, mono: PLEX_MONO, display: PLEX_DISPLAY },
  bodyWeight: 400,
  // Glass identity: tracked monospace ALLCAPS labels (technical).
  label: { case: "upper", tracking: "0.08em", font: "mono", weight: 500 },
  // Medium, not the old weight-200 hairline: a 24px page title must not
  // read lighter than the 16-20px semibold section heads under it
  // (docs/ui-guide/tokens.css: --display-weight 500).
  display: { weight: 500, letterSpacing: "-0.01em" },
  linkUnderline: false,
};

// ─── geometry: shared by every theme, never themed ───
//
// One root size and one type ramp, so a layout that fits in one theme
// fits in all of them. 12px is the floor. Emitted ONCE into :root by
// buildCss (before the per-theme blocks) as --root-size,
// --text-{key}-size|lh|ls (Tailwind's text-* ramp) and the px-named
// --fs-N / --lh-N pairs (docs/ui-guide/tokens.css) for inline styles.
//
//   text-xs 12/16 · text-sm 13/20 · text-base 14/20 · text-md 16/24
//   text-xl 20/28 · text-2xl 24/32 (page titles) · text-3xl 32/40
//   (key figures). text-2xs (12) and text-lg (16) are aliases kept so
//   existing call sites need no churn; 4xl/5xl are display-only.
const step = (size: number, lh: number, ls = "0"): RampStep => ({
  size: `${size}px`,
  lineHeight: `${lh}px`,
  letterSpacing: ls,
});

export const GEOMETRY: {
  rootSize: string;
  ramp: Record<RampKey, RampStep>;
  /** px-named pairs: --fs-12 / --lh-12 … --fs-32 / --lh-32. */
  scale: [number, number][];
} = {
  rootSize: "15px",
  ramp: {
    "2xs": step(12, 16),
    xs: step(12, 16),
    sm: step(13, 20),
    base: step(14, 20),
    md: step(16, 24),
    lg: step(16, 24),
    xl: step(20, 28, "-0.01em"),
    "2xl": step(24, 32, "-0.015em"),
    "3xl": step(32, 40, "-0.02em"),
    "4xl": step(40, 44, "-0.025em"),
    "5xl": step(56, 60, "-0.03em"),
  },
  scale: [
    [12, 16],
    [13, 20],
    [14, 20],
    [16, 24],
    [20, 28],
    [24, 32],
    [32, 40],
  ],
};

// Agent identity hues — one per agent, constant across every screen and
// every theme (docs/ui-guide/tokens.css). Emitted as --id-1 … --id-8.
export const IDENTITY_HUES = [
  "#8b7cf6",
  "#e26fb4",
  "#5b9cf0",
  "#e0736a",
  "#e59a45",
  "#4fb58a",
  "#3fb5c9",
  "#b5a642",
] as const;

// Radius scale: small and the same everywhere (2026-10-11: one crisp look
// on every page, the workflow run view included). xxl = cards; xl =
// buttons / rows / chips.
export const baseRadius: ThemeRadius = {
  sm: "3px",
  md: "5px",
  lg: "6px",
  xl: "6px",
  xxl: "8px",
};
